import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowRight, Search, Palette, Grid3x3, LogOut, History, Settings,
  ChevronRight, User
} from "lucide-react";
import { API } from "@/App";

export default function CatalogPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [wallpapers, setWallpapers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchCollections();
  }, []);

  // Auto-open collection from URL param (e.g., from homepage click)
  useEffect(() => {
    const colId = searchParams.get("col");
    if (colId && collections.length > 0 && !selectedCollection) {
      fetchWallpapers(colId);
      setSearchParams({}, { replace: true });
    }
  }, [collections, searchParams]);

  const fetchCollections = async () => {
    try {
      const res = await axios.get(`${API}/api/collections`, { withCredentials: true });
      setCollections(res.data);
    } catch {
      toast.error("Errore nel caricamento del catalogo");
    } finally {
      setLoading(false);
    }
  };

  const fetchWallpapers = async (collectionId) => {
    try {
      const res = await axios.get(`${API}/api/collections/${collectionId}`, { withCredentials: true });
      setSelectedCollection(res.data);
      setWallpapers(res.data.wallpapers || []);
    } catch {
      toast.error("Errore nel caricamento");
    }
  };

  const filteredCollections = collections.filter(c => {
    const matchFilter = filter === "all" || c.collection_type === filter;
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-stone-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="catalog-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img
              src="/logo-uol.png"
              alt="UOL Paper"
              className="h-16 w-auto cursor-pointer"
              onClick={() => { setSelectedCollection(null); setWallpapers([]); }}
            />
            {selectedCollection && (
              <div className="hidden sm:flex items-center gap-2 text-sm text-stone-400">
                <ChevronRight className="w-3 h-3" />
                <span>{selectedCollection.name}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="history-btn"
              variant="ghost"
              onClick={() => navigate("/storico")}
              className="rounded-none text-stone-600 hover:text-stone-900 h-9 px-3"
            >
              <History className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline text-xs uppercase tracking-widest">Storico</span>
            </Button>
            {user?.role === "admin" && (
              <Button
                data-testid="admin-btn"
                variant="ghost"
                onClick={() => navigate("/admin")}
                className="rounded-none text-stone-600 hover:text-stone-900 h-9 px-3"
              >
                <Settings className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline text-xs uppercase tracking-widest">Admin</span>
              </Button>
            )}
            <div className="flex items-center gap-2 pl-2 border-l border-stone-200">
              <span className="text-xs text-stone-500 hidden sm:block">{user?.name || user?.email}</span>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                onClick={onLogout}
                className="rounded-none h-9 px-2 text-stone-400 hover:text-red-600"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {!selectedCollection ? (
          <>
            {/* Collections View */}
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-light text-stone-900 mb-2">Catalogo</h1>
              <p className="text-sm text-stone-500">Scegli una collezione per visualizzare i soggetti disponibili</p>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  data-testid="search-input"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Cerca collezione..."
                  className="rounded-none pl-10 h-10 border-stone-200"
                />
              </div>
              <div className="flex gap-1">
                {[
                  { id: "all", label: "Tutte" },
                  { id: "baseline", label: "Baseline" },
                  { id: "artist", label: "Artist" },
                ].map(f => (
                  <Button
                    key={f.id}
                    data-testid={`filter-${f.id}`}
                    variant={filter === f.id ? "default" : "outline"}
                    onClick={() => setFilter(f.id)}
                    className={`rounded-none text-xs uppercase tracking-widest h-10 px-4 ${
                      filter === f.id ? "bg-stone-900 text-white" : "border-stone-200"
                    }`}
                  >
                    {f.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Collection Grid */}
            {filteredCollections.length === 0 ? (
              <div className="text-center py-20 text-stone-400">
                <Palette className="w-8 h-8 mx-auto mb-3" />
                <p>Nessuna collezione trovata. Contatta l'admin per popolare il catalogo.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredCollections.map(col => (
                  <div
                    key={col.collection_id}
                    data-testid={`collection-${col.collection_id}`}
                    className="group cursor-pointer border border-stone-200 hover:border-stone-400 transition-all bg-white overflow-hidden"
                    onClick={() => fetchWallpapers(col.collection_id)}
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-stone-100">
                      {col.cover_image_url ? (
                        <img
                          src={col.cover_image_url}
                          alt={col.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Grid3x3 className="w-8 h-8 text-stone-300" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-base font-medium text-stone-900">{col.name}</h3>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${
                          col.collection_type === "artist"
                            ? "bg-amber-50 text-amber-700"
                            : "bg-stone-100 text-stone-600"
                        }`}>
                          {col.collection_type === "artist" ? "Artist" : "Baseline"}
                        </span>
                      </div>
                      {col.artist_name && (
                        <p className="text-xs text-stone-500 flex items-center gap-1 mb-2">
                          <User className="w-3 h-3" /> {col.artist_name}
                        </p>
                      )}
                      <p className="text-xs text-stone-400 line-clamp-2 mb-3">{col.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-stone-900">
                          {col.price_per_sqm?.toFixed(2).replace('.', ',')} €/mq
                        </span>
                        <span className="text-xs text-stone-400">
                          {col.wallpaper_count || 0} soggetti
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Wallpapers View */}
            <div className="mb-8">
              <button
                data-testid="back-to-collections"
                onClick={() => { setSelectedCollection(null); setWallpapers([]); }}
                className="flex items-center gap-2 text-stone-500 hover:text-stone-900 transition-colors mb-4 text-sm"
              >
                <ChevronRight className="w-3 h-3 rotate-180" />
                <span className="uppercase tracking-widest text-xs">Tutte le collezioni</span>
              </button>
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-light text-stone-900 mb-1">{selectedCollection.name}</h1>
                  <p className="text-sm text-stone-500 max-w-2xl">{selectedCollection.description}</p>
                  <div className="flex items-center gap-4 mt-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${
                      selectedCollection.collection_type === "artist" ? "bg-amber-50 text-amber-700" : "bg-stone-100 text-stone-600"
                    }`}>
                      {selectedCollection.collection_type === "artist" ? "Artist Stripes" : "Baseline"}
                    </span>
                    <span className="text-sm font-medium text-stone-700">
                      {selectedCollection.price_per_sqm?.toFixed(2).replace('.', ',')} €/mq
                    </span>
                    {selectedCollection.artist_name && (
                      <span className="text-xs text-stone-500 flex items-center gap-1">
                        <User className="w-3 h-3" /> {selectedCollection.artist_name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Wallpaper Grid */}
            {wallpapers.length === 0 ? (
              <div className="text-center py-20 text-stone-400">
                <Palette className="w-8 h-8 mx-auto mb-3" />
                <p>Nessun soggetto in questa collezione</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {wallpapers.map(wp => (
                  <div
                    key={wp.wallpaper_id}
                    data-testid={`wallpaper-${wp.wallpaper_id}`}
                    className="group cursor-pointer border border-stone-200 hover:border-stone-400 transition-all bg-white overflow-hidden"
                    onClick={() => navigate(`/simulatore/${wp.wallpaper_id}`)}
                  >
                    <div className="aspect-square overflow-hidden bg-stone-100 relative">
                      {wp.image_url ? (
                        <img
                          src={wp.image_url}
                          alt={wp.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Palette className="w-8 h-8 text-stone-300" />
                        </div>
                      )}
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="bg-white px-4 py-2 flex items-center gap-2">
                          <span className="text-xs uppercase tracking-widest text-stone-800 font-medium">Simula</span>
                          <ArrowRight className="w-3 h-3 text-stone-800" />
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-stone-900 truncate">{wp.name}</p>
                      <p className="text-xs text-stone-400 mt-1">
                        {selectedCollection.price_per_sqm?.toFixed(2).replace('.', ',')} €/mq
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
